import {
  CompanyLocation,
  getFoodControlPointInstancesByCurrentDate,
  getFoodControlPointsFromLocationId,
} from "@enos-monorepo/lib";
import {
  FoodControlPoint,
  FoodControlPointInstance,
  FoodControlPointInstanceDto,
} from "@enos-monorepo/lib/types/FoodManagementTypes";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export function mergeInstanceWithFoodControl(
  controlPoints: FoodControlPoint[] = [],
  instances: FoodControlPointInstance[] = [],
): FoodControlPointInstanceDto[] {
  const controlPointMap: Record<string, FoodControlPoint> = {};

  if (controlPoints.length > 0 && instances.length > 0) {
    controlPoints.forEach((controlPoint) => {
      controlPointMap[controlPoint.id] = controlPoint;
    });

    return instances.map((instance) => ({
      ...instance,
      controlPoint: controlPointMap[instance.foodControlPointId] ?? undefined,
    }));
  }

  return [];
}
export function useFoodControls(selectedLocation?: CompanyLocation) {
  const today = useMemo(() => new Date(), []);

  const { data: controlPoints } = useQuery({
    queryKey: ["foodControlPoints", selectedLocation?.id],
    queryFn: () =>
      getFoodControlPointsFromLocationId(selectedLocation?.id ?? ""),
    enabled: !!selectedLocation,
  });

  const { data: controlPointInstances } = useQuery({
    queryKey: [
      "foodControlPointInstances",
      selectedLocation?.id,
      today.toISOString(),
    ],
    queryFn: () =>
      getFoodControlPointInstancesByCurrentDate(selectedLocation?.id ?? ""),
    enabled: !!selectedLocation && !!today,
  });

  const filteredControlPoints = useMemo(
    () => controlPoints?.filter((controlPoint) => !controlPoint.isDeleted),
    [controlPoints],
  );

  const filteredControlPointInstances = useMemo(
    () => controlPointInstances?.filter((instance) => !instance.isDeleted),
    [controlPointInstances],
  );

  const mergedData = useMemo(
    () =>
      mergeInstanceWithFoodControl(
        filteredControlPoints,
        filteredControlPointInstances,
      ),
    [filteredControlPoints, filteredControlPointInstances],
  );

  return {
    foodControlPoints: mergedData,
    controlPointInstances: filteredControlPointInstances,
    controlPoints: filteredControlPoints,
  };
}
